<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'UcRegisterEntryInvoiceSelector',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
    editor: {
      type: Object,
      required: true,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const onOptionSelected = (...args) => {
    const [{ option, value }] = args;
    if (_.isObject(option)) {
      props.editor.invoice = _.cloneDeep(option);
    }
  };

  const messages = ref({
    searchPlaceholder: $fn.value.pgettext('Register', 'Search for invoice'),
  });
  const ux = ref([]);
  const b_2248226175642056_dataSource = computed(() => ({
    name: 'suggest_invoice',
    parameters: {
      company_id: props.editor.version.company_id,
      partner_id: props.editor.partner ? props.editor.partner.id : null,
    },
  }));

  const $data = computed(() => ({
    messages: messages.value,
    ux: ux.value,
    b_2248226175642056_dataSource: b_2248226175642056_dataSource.value,
  }));

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'messages' },
      { name: 'ux' },
      { name: 'b_2248226175642056_dataSource', nodeId: '2248226175642056' },
    ],
    loaders: {},
    ux: {},
    dataRefs: { messages, ux, b_2248226175642056_dataSource },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  watch(
    () => props.editor,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('editor');
    }
  );

  const act_2248226175642056_script_dv00qytz2_value_callable = async (
    $event
  ) => {
    return onOptionSelected($event);
  };
  const act_2248226175642056_script_dv00qytz2_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_2248226175642056_script_dv00qytz2_value_callable($event);
      },
    };
  };
  const act_5534025912102772_emit_9uhlp750f_getActionArgs = async ($event) => {
    return {
      event: 'close',
    };
  };
  const act_5534025912102772_showModal_f56lfxlhw_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'CreateInvoice',
      props: {
        registerEntry: props.editor.id,
      },
    };
  };

  const act_7315092382398562_reloadComponentData_wgsziamgq =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_mrcssi38q =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_wgsziamgq,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_mrcssi38q],
    event: 'reload',
  });
  const act_2248226175642056_script_dv00qytz2 = new actionTypes.ScriptAction({
    actionArgs: act_2248226175642056_script_dv00qytz2_getActionArgs,
  });
  const inst_evh_2248226175642056_optionSelected_8ujfwwtwr =
    new actionTypes.EventHandlerImpl({
      action: act_2248226175642056_script_dv00qytz2,
      event: 'optionSelected',
      displayName: 'script',
    });
  const inst_evh_2248226175642056_optionSelected =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2248226175642056_optionSelected_8ujfwwtwr],
      event: 'optionSelected',
    });
  const act_5534025912102772_emit_9uhlp750f = new actionTypes.EmitAction({
    actionArgs: act_5534025912102772_emit_9uhlp750f_getActionArgs,
  });
  const inst_evh_5534025912102772_clickPreventStop_jzjeqgqvq =
    new actionTypes.EventHandlerImpl({
      action: act_5534025912102772_emit_9uhlp750f,
      event: 'click.prevent.stop',
      displayName: 'emit',
    });
  const act_5534025912102772_showModal_f56lfxlhw =
    new actionTypes.ShowModalAction({
      actionArgs: act_5534025912102772_showModal_f56lfxlhw_getActionArgs,
    });
  const inst_evh_5534025912102772_clickPreventStop_6ulf8z18e =
    new actionTypes.EventHandlerImpl({
      action: act_5534025912102772_showModal_f56lfxlhw,
      event: 'click.prevent.stop',
      displayName: 'showModal',
    });
  const inst_evh_5534025912102772_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [
        inst_evh_5534025912102772_clickPreventStop_jzjeqgqvq,
        inst_evh_5534025912102772_clickPreventStop_6ulf8z18e,
      ],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_reload_mrcssi38q,
    inst_evh_7315092382398562_reload,
    inst_evh_2248226175642056_optionSelected_8ujfwwtwr,
    inst_evh_2248226175642056_optionSelected,
    inst_evh_5534025912102772_clickPreventStop_jzjeqgqvq,
    inst_evh_5534025912102772_clickPreventStop_6ulf8z18e,
    inst_evh_5534025912102772_clickPreventStop,
  };

  const evh_7315092382398562_reload_mrcssi38q = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_mrcssi38q.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_optionSelected_8ujfwwtwr = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2248226175642056_optionSelected_8ujfwwtwr.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_optionSelected = async (event, scope) => {
    return await actions.inst_evh_2248226175642056_optionSelected.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5534025912102772_clickPreventStop_jzjeqgqvq = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_5534025912102772_clickPreventStop_jzjeqgqvq.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5534025912102772_clickPreventStop_6ulf8z18e = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_5534025912102772_clickPreventStop_6ulf8z18e.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5534025912102772_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_5534025912102772_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpUsercontrol
    :loading="loading"
    :reloading="reloading"
    :loaded="loaded"
    :error="error"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <ItpFormSearchableSelect
      name="invoice"
      v-model="editor.invoice"
      bind-object
      :placeholder="messages.searchPlaceholder"
      :dataSource="b_2248226175642056_dataSource"
      valueField="id"
      textField="invoice_number"
      clearable
      optionTemplate="<UcSuggestInvoiceOption :option=option></UcSuggestInvoiceOption>"
      selectedOptionTemplate='<UcSuggestInvoiceOption v-if="option.id" :option=option></UcSuggestInvoiceOption>'
      :readonly="editor.isReadonly"
      @optionSelected="evh_2248226175642056_optionSelected($event, {})"
    >
    </ItpFormSearchableSelect>
    <ItpBox direction="column" v-if="loaded">
      <ItpBox
        v-if="
          editor.invoice &&
          !editor.invoice.id &&
          editor.$_resource.invoice &&
          editor.$_resource.invoice.invoice_number
        "
      >
        <ItpButton
          :text="pgettext('RegisterEntry', 'Create invoice...')"
          size="sm"
          variant="light"
          :tooltip="pgettext('RegisterEntry', 'Record a new invoice.')"
          :icon="$config.addIcon"
          v-if="editor.id"
          @click.prevent.stop="
            evh_5534025912102772_clickPreventStop($event, {})
          "
        >
        </ItpButton>
      </ItpBox>
    </ItpBox>
  </ItpUsercontrol>
</template>
